import React, { useState } from "react";
import DataTable from "react-data-table-component";
import { Button, Input, InputGroup, InputGroupText } from "reactstrap";
import { Search, Plus, Edit, Trash2, Eye, RefreshCw } from "react-feather";

/**
 * ModuleDataTable - Reusable data table with actions
 *
 * @param {Object} props
 * @param {Array} props.data - Array of data objects
 * @param {Array} props.columns - Array of column definitions
 * @param {Object} props.actions - Object containing action handlers
 * @param {string} props.title - Table title
 * @param {boolean} props.loading - Loading state
 */
const ModuleDataTable = ({
  data = [],
  columns = [],
  actions = {},
  title = "",
  loading = false,
  searchable = true,
  addButton = true,
  refreshButton = true,
}) => {
  const [searchText, setSearchText] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);

  // Filter data based on search
  const filteredData = searchable
    ? data.filter((item) => {
        const searchLower = searchText.toLowerCase();
        return Object.values(item).some((value) =>
          String(value).toLowerCase().includes(searchLower)
        );
      })
    : data;

  const handleRowSelected = (state) => {
    setSelectedRows(state.selectedRows);
  };

  // Action column
  const actionColumn = {
    name: "Actions",
    cell: (row) => (
      <div className="d-flex gap-2">
        {actions.onView && (
          <Button
            color="info"
            size="sm"
            outline
            onClick={() => actions.onView(row)}
            title="View"
          >
            <Eye size={14} />
          </Button>
        )}
        {actions.onEdit && (
          <Button
            color="warning"
            size="sm"
            outline
            onClick={() => actions.onEdit(row)}
            title="Edit"
          >
            <Edit size={14} />
          </Button>
        )}
        {actions.onDelete && (
          <Button
            color="danger"
            size="sm"
            outline
            onClick={() => actions.onDelete(row)}
            title="Delete"
          >
            <Trash2 size={14} />
          </Button>
        )}
        {actions.onSyncProject && (
          <Button
            color="primary"
            size="sm"
            outline
            onClick={() => actions.onSyncProject(row)}
            disabled={actions.isSyncingProject?.(row)}
            title="Sync Points"
          >
            {actions.isSyncingProject?.(row)
              ? <span className="spinner-border spinner-border-sm" style={{ width: "0.75rem", height: "0.75rem" }} />
              : <RefreshCw size={14} />}
          </Button>
        )}
      </div>
    ),
    ignoreRowClick: true,
    allowOverflow: true,
    button: true,
    width: "150px",
  };

  const tableColumns = actions.onView || actions.onEdit || actions.onDelete
    ? [...columns, actionColumn]
    : columns;

  return (
    <div className="module-data-table">
      {/* Header with Title and Actions */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          {title && <h5 className="mb-0">{title}</h5>}
        </div>
        <div className="d-flex gap-2">
          {refreshButton && actions.onRefresh && (
            <Button
              color="secondary"
              size="sm"
              onClick={actions.onRefresh}
              disabled={loading}
            >
              <RefreshCw size={14} className="me-1" />
              Refresh
            </Button>
          )}
          {addButton && actions.onAdd && (
            <Button
              color="primary"
              size="sm"
              onClick={actions.onAdd}
            >
              <Plus size={14} className="me-1" />
              Add New
            </Button>
          )}
        </div>
      </div>

      {/* Search Bar */}
      {searchable && (
        <div className="mb-3">
          <InputGroup>
            <InputGroupText>
              <Search size={14} />
            </InputGroupText>
            <Input
              type="text"
              placeholder="Search..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </InputGroup>
        </div>
      )}

      {/* Bulk Actions */}
      {selectedRows.length > 0 && actions.onBulkDelete && (
        <div className="mb-3">
          <Button
            color="danger"
            size="sm"
            onClick={() => actions.onBulkDelete(selectedRows)}
          >
            <Trash2 size={14} className="me-1" />
            Delete Selected ({selectedRows.length})
          </Button>
        </div>
      )}

      {/* Data Table */}
      <DataTable
        columns={tableColumns}
        data={filteredData}
        pagination
        paginationPerPage={10}
        paginationRowsPerPageOptions={[10, 20, 30, 50]}
        highlightOnHover
        striped
        responsive
        progressPending={loading}
        selectableRows={actions.onBulkDelete !== undefined}
        onSelectedRowsChange={handleRowSelected}
        noDataComponent={
          <div className="text-center py-4">
            <p className="text-muted">No data available</p>
          </div>
        }
      />
    </div>
  );
};

export default ModuleDataTable;
